import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "chart.js/auto";
import "./GraphCards.css";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Person2RoundedIcon from "@mui/icons-material/Person2Rounded";
import StarIcon from "@mui/icons-material/Star";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useOutsideClick } from "../../../../hooks/useOutsideClick";
import { useSelector } from "react-redux";
import { SelectBook } from "./SelectBook";
import { useCloseOnCompanySwitch } from "../../../../utils/companyContextNavigation";

const ZERO_CHART_COLOR = ["#c3c3c3"];

const chartDataKey = (values = []) => values.join("|");

export const GraphCards = React.memo(function GraphCards({
  item,
  isUser,
  isBooks,
  bookData,
  _id,
  defaultData,
  isStar,
  isRefreshing = false,
}) {
  const chartCanvasRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const chartDatasetKeyRef = useRef("");
  // Only the books card should react to book selection changes.
  const selectBooks = useSelector((state) =>
    isBooks ? state.theme.selectBooks : null,
  );
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const [select, setSelect] = useState(false);
  const [bookSelectOpen, setBookSelectOpen] = useState(false);
  const ref = useRef(null);

  useCloseOnCompanySwitch(() => {
    setBookSelectOpen(false);
  });

  const closePopup = () => {
    setSelect(false);
  };

  useOutsideClick(ref, closePopup);

  const handleMouseEnter = () => {
    setTooltipOpen(true);
  };

  const handleMouseLeave = () => {
    setTooltipOpen(false);
  };

  const chartData = useMemo(() => {
    const fromDetails =
      item?.details?.map((detail) => Number(detail?.values) || 0) ?? [];
    if (fromDetails.length > 0) return fromDetails;
    return item?.dataValue ?? [];
  }, [item?.details, item?.dataValue]);

  const checkAllZero = chartData?.every((value) => value === 0);
  const datasetValues = checkAllZero ? defaultData || [] : chartData;
  const datasetColors = checkAllZero
    ? ZERO_CHART_COLOR
    : item?.backgroundColor || [];
  const datasetKey = `${chartDataKey(datasetValues)}::${chartDataKey(
    datasetColors,
  )}`;

  const selectedBookName = selectBooks?.name ?? "Select";
  const dropdownMaxWidth = Math.min(
    Math.max(selectedBookName.length * 8 + 44, 100),
    200,
  );

  useEffect(() => {
    const canvas = chartCanvasRef.current;
    if (!canvas) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = new Chart(canvas, {
        type: "doughnut",
        data: {
          datasets: [
            {
              data: datasetValues,
              backgroundColor: datasetColors,
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          animation: {
            animateRotate: true,
            animateScale: true,
            duration: 800,
          },
        },
      });
      chartDatasetKeyRef.current = datasetKey;
      return;
    }

    // Skip no-op updates so unrelated re-renders don't replay the animation.
    if (chartDatasetKeyRef.current === datasetKey) return;

    chartDatasetKeyRef.current = datasetKey;
    const chart = chartInstanceRef.current;
    chart.data.datasets[0].data = datasetValues;
    chart.data.datasets[0].backgroundColor = datasetColors;
    chart.update();
  }, [datasetKey, datasetValues, datasetColors]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.destroy();
      chartInstanceRef.current = null;
    };
  }, []);

  return (
    <div
      className={`graphCards${isRefreshing ? " graphCards--refreshing" : ""}`}
    >
      {isBooks ? (
        <SelectBook
          activityOpen={bookSelectOpen}
          setActivityOpen={setBookSelectOpen}
          allBooks={bookData}
        />
      ) : null}
      {isUser && (
        <div
          className={
            isTooltipOpen
              ? "graphCards__infoTooltipWrap graphCards__infoTooltipWrap--open"
              : "graphCards__infoTooltipWrap"
          }
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="graphCards__infoTooltipAnchor">
            <InfoOutlinedIcon
              sx={{
                color: "var(--syellow)",
                fontSize: "20px",
                display: "block",
              }}
            />
            {isTooltipOpen && (
              <div className="popperOver">
                <h6>Total Users</h6>
                Active: At least 1 org or company book completed in the last 2
                months
                <br />
                <span> (Either Company or Organizational).</span>
                <br /> Inactive: No org or company book completed in the last 2
                months <br />
              </div>
            )}
          </div>
        </div>
      )}
      <div className="graphCards__headLayout">
        <h5 className="ftwt__b" style={{ color: "#444444" }}>
          {item?.title}
        </h5>
        {isBooks && bookData && bookData?.length > 0 && (
          <div
            onClick={() => setBookSelectOpen(true)}
            className="graphCards__selectLayout"
            style={{ "--graph-card-select-max-width": `${dropdownMaxWidth}px` }}
          >
            <h6 className="ftwt__r">{selectedBookName}</h6>
            <ArrowForwardIosIcon
              sx={{
                transform: select ? "rotate(-90deg)" : "rotate(90deg)",
                color: "grey",
                fontSize: "12px !important",
              }}
            />
          </div>
        )}
      </div>
      <div className="graphCards__details">
        <div className="graphCards__chart">
          <canvas ref={chartCanvasRef} />
          <div className="graphCards__inner-shadow"></div>
        </div>

        <div className="graphCards__detailscount">
          {item?.details?.map((detail, index) => (
            <div className="graphCards__content" key={`${detail?.label}-${index}`}>
              <RadioButtonCheckedIcon
                sx={{ color: detail?.color, fontSize: "15px" }}
              />
              <h6>{detail?.label}</h6>
              <h6 className="ftwt__r  graphCards__contentValues">
                {detail?.values}
              </h6>
            </div>
          ))}
        </div>
      </div>

      <div className="graphCards__bottom">
        {!isStar && (
          <Person2RoundedIcon
            sx={{ color: "var(--pblue)", fontSize: "20px" }}
          />
        )}
        {isStar && (
          <StarIcon sx={{ color: "var(--sstar)", fontSize: "20px" }} />
        )}
        <h6 className="ftwt__b">{item?.footerLabel}</h6>
      </div>
    </div>
  );
});
